import { useTranslation } from 'react-i18next'
import { X, Play, FileText, Clock, Users, User as UserIcon, RefreshCw, Award } from 'lucide-react'
import { SubjectGlyph } from '@/components/aurora'
import MobileAppBanner from '@/components/MobileAppBanner'

/**
 * Exam-detail modal shown before a session starts. Tapping an exam no longer
 * drops the student straight into a running (timed) attempt — this surfaces
 * the title/subject/teacher/duration/description first, and only the explicit
 * "Boshlash" button here calls onStart to begin. Mirrors the mobile app's
 * exam-info bottom sheet.
 */
function fmtDate(iso, locale) {
  if (!iso) return null
  return new Date(iso).toLocaleString(locale || undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

export default function ExamInfoModal({ exam, onClose, onStart }) {
  const { t, i18n } = useTranslation()
  if (!exam) return null

  const rows = [
    exam.question_count != null && {
      icon: FileText,
      label: t('exams.questions', 'Savollar'),
      value: `${exam.question_count} ${t('exams.questionsShort', 'savol')}`,
    },
    exam.time_limit_minutes && {
      icon: Clock,
      label: t('exams.duration', 'Davomiyligi'),
      value: `${exam.time_limit_minutes} ${t('exams.minutesShort', 'daq')}`,
    },
    exam.participant_count != null && {
      icon: Users,
      label: t('exams.participants', 'ishtirokchi'),
      value: `${exam.participant_count}`,
    },
    (exam.allowed_attempts ?? 1) > 1 && {
      icon: RefreshCw,
      label: t('exams.attemptsLabel', 'Urinishlar'),
      value: `${exam.allowed_attempts}`,
    },
  ].filter(Boolean)

  const startAt = fmtDate(exam.start_time, i18n.language)
  const endAt   = fmtDate(exam.end_time, i18n.language)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 space-y-5"
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-3">
          <SubjectGlyph subject={exam.primary_subject} examTitle={exam.title} size="md" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-bold text-lg m-0 leading-snug" style={{ color: 'var(--ink)' }}>
                {exam.title}
              </h2>
              {exam.is_national_cert && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: 'var(--butter-soft)', color: 'var(--butter)' }}>
                  <Award size={11} strokeWidth={2} /> {t('exams.nationalCert', 'Sertifikat')}
                </span>
              )}
            </div>
            {exam.primary_subject?.name && (
              <p className="text-[13px] font-semibold m-0 mt-0.5" style={{ color: 'var(--coral)' }}>
                {exam.primary_subject.name}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors flex-shrink-0"
            style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--ink-3)' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Info grid */}
        {rows.length > 0 && (
          <div className="grid grid-cols-2 gap-2.5">
            {rows.map(({ icon: Icon, label, value }) => (
              <div key={label} className="rounded-xl px-3.5 py-2.5"
                style={{ background: 'var(--bg-soft)', border: '1px solid var(--border)' }}>
                <div className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: 'var(--ink-3)' }}>
                  <Icon size={12} strokeWidth={2} /> {label}
                </div>
                <div className="text-sm font-bold mt-0.5" style={{ color: 'var(--ink)' }}>{value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Teacher */}
        <div className="flex items-center gap-1.5 text-[13px]" style={{ color: 'var(--ink-2)' }}>
          <UserIcon size={13} strokeWidth={2} style={{ color: 'var(--ink-3)' }} />
          <span style={{ color: 'var(--ink-3)' }}>{t('exams.teacher', 'O\'qituvchi')}:</span>
          <span className="font-semibold" style={{ color: 'var(--ink)' }}>
            {exam.teacher?.full_name || exam.teacher?.username || '—'}
          </span>
        </div>

        {/* Time window */}
        {(startAt || endAt) && (
          <div className="text-[12px] font-mono" style={{ color: 'var(--ink-3)' }}>
            {startAt && <div>{t('exams.opensAt', 'Ochilish')}: {startAt}</div>}
            {endAt && <div>{t('exams.closesAt', 'Yopilish')}: {endAt}</div>}
          </div>
        )}

        {/* Description */}
        {exam.description && (
          <p className="text-[13px] leading-relaxed m-0" style={{ color: 'var(--ink-2)' }}>
            {exam.description}
          </p>
        )}

        {/* "Better in the app" — mobile browsers only, when store links exist */}
        <MobileAppBanner variant="prominent" />

        {/* Start */}
        <button
          onClick={onStart}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white transition-all"
          style={{ background: 'var(--coral)', border: 0, cursor: 'pointer' }}
        >
          <Play size={15} strokeWidth={2} />
          {t('exams.start', 'Boshlash')}
        </button>
      </div>
    </div>
  )
}
